import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";

const Privacy = () => (
  <div className="min-h-screen bg-slate-50">
    <Navbar />
    <article className="max-w-3xl mx-auto px-6 py-12 lg:py-20">
      <div className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 mb-2">Privacy</div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Privacy Policy</h1>
      <p className="mt-3 text-sm text-slate-500">Last updated: February 8, 2026</p>

      <div className="mt-10 space-y-8 text-slate-700 leading-relaxed">
        <p>The River Company (&ldquo;TRC&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) operates the Enterprise Alignment Readiness Assessment (the &ldquo;Assessment&rdquo;). This policy explains what we collect when you use it, how we use it, and the rights you have over your data.</p>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">What we collect</h2>
          <p>When you take the full Assessment, we collect information you provide:</p>
          <ul className="list-disc pl-6 mt-3 space-y-1 text-slate-600">
            <li>Identifying information: name, work email, organization, role or title</li>
            <li>Profile information: industry, company size, organization type, current systems and tools</li>
            <li>Free-text responses to two prompts: your biggest transformation challenge and your long-term objective</li>
            <li>Your follow-up preference</li>
            <li>Your twelve numerical answers (1&ndash;5 scale) and the calculated category averages, raw score, overall score, and readiness band</li>
          </ul>
          <p className="mt-4">If you take the 60-second pulse via a popup or website embed, we collect only: your email address, an optional name, and four numerical answers.</p>
          <p className="mt-4">We do not collect passwords, financial information, government identifiers, or any sensitive personal categories under GDPR.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">How we use it</h2>
          <ul className="list-disc pl-6 space-y-1 text-slate-600">
            <li>To compute your readiness score and generate the report you see on screen</li>
            <li>To follow up if you indicated interest in a conversation, or if your responses suggest we can be of service</li>
            <li>To improve the Assessment internally &mdash; never in identifiable form, never sold or shared</li>
            <li>Aggregate, anonymized scores may be used for benchmarking or industry research</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Where it is stored</h2>
          <p>Submissions are stored in a database operated by TRC. Access is restricted to authorized TRC team members and is protected by authentication.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">How long we keep it</h2>
          <p>We retain submissions for 24 months from the date you submit. After that period, we delete or fully anonymize the record.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Third-party services</h2>
          <p>We use a small number of processors to operate the Assessment:</p>
          <ul className="list-disc pl-6 mt-3 space-y-1 text-slate-600">
            <li><strong>MongoDB</strong> &mdash; database</li>
            <li><strong>OpenAI</strong> (via Emergent Integrations) &mdash; generates the narrative section of your on-screen report from your scored results</li>
            <li><strong>Resend</strong> &mdash; sends internal lead notifications to our team</li>
            <li><strong>Calendly</strong> &mdash; only if you choose to schedule a call</li>
          </ul>
          <p className="mt-4">We do not provide your data to advertising platforms or data brokers.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Your rights</h2>
          <p>You may request at any time:</p>
          <ul className="list-disc pl-6 mt-3 space-y-1 text-slate-600">
            <li>A copy of the data we hold on you</li>
            <li>Correction of inaccurate data</li>
            <li>Deletion of your data (the &ldquo;right to be forgotten&rdquo;)</li>
            <li>Withdrawal of any future processing of your data</li>
          </ul>
          <p className="mt-4">To make a request, email <a className="text-blue-600 hover:underline" href="mailto:paul@therivercompany.co">paul@therivercompany.co</a> with the subject line &ldquo;Assessment data request&rdquo; and the email address you used to submit. We respond within 30 days.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Children</h2>
          <p>The Assessment is intended for enterprise leaders aged 18 and over. It is not designed for or directed at children.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Changes to this policy</h2>
          <p>We may update this policy as our practices evolve. We will update the &ldquo;Last updated&rdquo; date above when changes are made.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Contact</h2>
          <p>Questions about this policy or your data: <a className="text-blue-600 hover:underline" href="mailto:paul@therivercompany.co">paul@therivercompany.co</a></p>
        </section>
      </div>

      <div className="mt-12 pt-8 border-t border-slate-200">
        <Link to="/" className="text-sm text-blue-600 hover:underline">&larr; Back to the Assessment</Link>
      </div>
    </article>
  </div>
);

export default Privacy;
